import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Link, withRouter } from 'react-router-dom'
import {
  Button,
  Tag,
  Card,
  Descriptions,
  Timeline,
  Empty,
  Divider,
  Space,
  Typography,
  Modal,
  Select,
  Input,
  InputNumber
} from 'antd'
import {
  HistoryOutlined,
  PhoneOutlined,
  ReloadOutlined,
  WhatsAppOutlined
} from '@ant-design/icons'
import PageLoader from '../../../components/Layouts/PageLoader'
import {
  getRequest,
  postRequest,
  patchRequest,
  dutyConversationsUrl,
  dutyCarRequestsUrl,
  dutyCarRequestFinalizeUrl,
  dutyCarRequestTriggerUrl,
  dutyCarRequestTriggerStepsUrl,
  dutyConversationTriggerStepsUrl,
  dutyConversationTriggerUrl,
  dutyTransactionsUrl,
  successNotification,
  errorNotification
} from '../../../config'
import CarEditForm from './CarEditForm'
import MessageComposer from './MessageComposer'
import StepTrigger, { stepOptions } from './StepTrigger'
import CustomerDetailBody from '../Customers/CustomerDetailBody'
import PaymentTimelineModal from '../../../components/PaymentTimelineModal'
import LightBox from '../../../components/LightBox'
import IdentityLabel from '../../../components/IdentityLabel'
import DecisionBanner from './DecisionBanner'
import DecisionLog from './DecisionLog'
import { parkedLine } from '../decisions'
import {
  channelTag,
  conversationPath,
  isAppChannel,
  serviceLabel,
  serviceTag
} from '../statusColors'
import {
  PAYMENT_MODE_COLORS,
  PAYMENT_MODE_LABELS,
  claimedName
} from '../../Transactions/utils'

const { Option } = Select

const STATE_COLORS = {
  collecting: 'blue',
  awaiting_payment: 'gold',
  paid: 'green',
  processing: 'orange',
  completed: 'green',
  failed: 'red',
  cancelled: 'default',
  ready: 'cyan'
}

// Mirror the backend payments constants (STATUS_LIST / PAYMENT_METHODS).
const TXN_STATUS_OPTIONS = ['INITIALIZED', 'PROCESSING', 'SUCCESSFUL', 'CANCELLED']
const TXN_METHOD_OPTIONS = [
  { value: 'CASH', label: 'Cash / Offline' },
  { value: 'MOBILE_MONEY', label: 'Mobile Money' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'DEBIT_CARD', label: 'Debit Card' }
]
const CURRENCY_OPTIONS = ['GHS', 'USD', 'NGN']

const txnColor = status =>
  status === 'SUCCESSFUL'
    ? 'green'
    : status === 'CANCELLED'
    ? 'red'
    : 'gold'

/**
 * Where the other half of a switch or a hand-off is (spec: assistant-front-door Req 2.2).
 *
 * A link only when we have the id. The old conversation records the service it switched to the
 * moment it switches; the new conversation's id arrives with it, but a row written before that
 * was stored has only the service — and a link that goes nowhere is worse than plain text.
 */
const ConversationLink = ({ id, label, service }) =>
  id ? (
    <Link to={conversationPath(service, id)}>
      <Typography.Text type="secondary" underline>
        {label}
      </Typography.Text>
    </Link>
  ) : (
    <Typography.Text type="secondary">{label}</Typography.Text>
  )

ConversationLink.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  service: PropTypes.string
}

const CarPanel = ({
  car,
  onEdit,
  onFinalize,
  finalizing,
  triggerSteps,
  onTrigger,
  triggering
}) => {
  const slots = car.slots || {}
  const slotKeys = Object.keys(slots).filter(k => !k.startsWith('_'))
  const [step, setStep] = useState(undefined)
  return (
    <Card
      size="small"
      style={{ marginBottom: 12 }}
      title={`Car ${car.position} — `}
      extra={<Tag color={STATE_COLORS[car.status] || 'default'}>{car.status}</Tag>}
    >
      <Descriptions size="small" column={1} bordered>
        {car.priceAmount && (
          <Descriptions.Item label="Price">
            {car.priceCurrency || 'GHS'} {car.priceAmount}
          </Descriptions.Item>
        )}
        {slotKeys.map(k => (
          <Descriptions.Item key={k} label={k}>
            {String(slots[k]?.value)}
            {slots[k]?.confirmed ? ' ✓' : ''}
          </Descriptions.Item>
        ))}
      </Descriptions>

      {car.autoSelectedSlots && car.autoSelectedSlots.length ? (
        <div style={{ marginTop: 8 }}>
          <Tag color="geekblue">
            Auto-selected (only option): {car.autoSelectedSlots.join(', ')}
          </Tag>
        </div>
      ) : null}

      <div style={{ marginTop: 8 }}>
        <Space wrap>
          <Button size="small" onClick={() => onEdit(car)}>
            Edit Details
          </Button>
          <Button
            size="small"
            type="primary"
            loading={finalizing}
            onClick={() => onFinalize(car)}
          >
            Process / Finalize
          </Button>
          {car.dutyDocumentUrl && (
            <Button
              size="small"
              onClick={() => window.open(car.dutyDocumentUrl, '_blank', 'noopener')}
            >
              View PDF
            </Button>
          )}
          {/* Companion PNG image disabled for now — PDF only.
          {car.dutyDocumentImageUrl && (
            <Button
              size="small"
              onClick={() =>
                window.open(car.dutyDocumentImageUrl, '_blank', 'noopener')
              }
            >
              View Image
            </Button>
          )} */}
        </Space>
      </div>

      {/* Manual step control: (re-)trigger any assistant step — available for ANY status,
          including cancelled/completed (a cancelled car is reopened on the server). */}
      <div style={{ marginTop: 8 }}>
        <Space wrap>
          <Select
            size="small"
            showSearch
            allowClear
            optionFilterProp="label"
            placeholder="Manual: trigger a step"
            style={{ minWidth: 240 }}
            value={step}
            onChange={setStep}
            options={stepOptions(triggerSteps)}
          />
          <Button
            size="small"
            disabled={!step}
            loading={triggering}
            onClick={() => onTrigger(car, step)}
          >
            Send step
          </Button>
        </Space>
        <div style={{ color: '#888', fontSize: 12, marginTop: 4 }}>
          Re-ask a question, resend the Pay Now button, re-derive the body type, or
          advance — for a stuck or manually-assisted chat. Works on cancelled/completed cars too
          (a cancelled car is reopened).
        </div>
      </div>

      {/* Diagnostic signals for a stuck car. */}
      {car.status === 'failed' && (
        <Tag color="red" style={{ marginTop: 8 }}>
          Finalization failed{car.flaggedForReview ? ' — flagged for review' : ''}
        </Tag>
      )}
      {car.status === 'awaiting_payment' && (
        <Tag color="gold" style={{ marginTop: 8 }}>
          Awaiting payment
        </Tag>
      )}
    </Card>
  )
}

CarPanel.propTypes = {
  car: PropTypes.object,
  onEdit: PropTypes.func,
  onFinalize: PropTypes.func,
  finalizing: PropTypes.bool,
  triggerSteps: PropTypes.array,
  onTrigger: PropTypes.func,
  triggering: PropTypes.bool
}

/**
 * The order or shipment a history or tracking conversation created, and where the customer is
 * (owner, 2026-10-29). Before the order exists there is only the step, which is the useful thing
 * to know: a customer stuck choosing a country needs a different nudge from one stuck paying.
 */
const RECORD_PATHS = {
  vehicle_history: id => `/vehicle-history/orders/${id}`,
  tracking: id => `/tracking/shipments/${id}`
}

const ServiceRecordCard = ({ service, record, progress }) => (
  <Card size="small" style={{ marginBottom: 12 }}>
    <Descriptions size="small" column={{ xs: 1, sm: 2 }}>
      <Descriptions.Item label="Where they are">
        {progress || <span style={{ color: '#888' }}>—</span>}
      </Descriptions.Item>
      {record ? (
        <>
          <Descriptions.Item label={service === 'tracking' ? 'Shipment' : 'Report'}>
            {record.title}
            {record.subtitle ? (
              <Typography.Text type="secondary"> · {record.subtitle}</Typography.Text>
            ) : null}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Space size={4} wrap>
              <Tag>{record.statusLabel || record.status}</Tag>
              {record.isPaid ? <Tag color="green">Paid</Tag> : null}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Price">
            {record.amount ? `${record.currency || 'GHS'} ${record.amount}` : '—'}
          </Descriptions.Item>
        </>
      ) : null}
    </Descriptions>
    {record && RECORD_PATHS[service] ? (
      <Link to={RECORD_PATHS[service](record.id)}>
        <Button size="small" type="primary" style={{ marginTop: 8 }}>
          {service === 'tracking' ? 'Open the shipment' : 'Open the order'}
        </Button>
      </Link>
    ) : (
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        No {service === 'tracking' ? 'shipment' : 'order'} yet — it is created once the customer
        reaches the summary.
      </Typography.Text>
    )}
  </Card>
)

ServiceRecordCard.propTypes = {
  service: PropTypes.string,
  record: PropTypes.object,
  progress: PropTypes.string
}

/**
 * One conversation, in full.
 *
 * Rendered two ways, and the difference is only chrome: as a routed PAGE (the default), and as the
 * body of the list's quick-view MODAL. Everything that reads or changes the conversation is the
 * same in both, because it is the same component — the alternative was a second, thinner detail
 * view that would drift the first time either was touched.
 *
 * `mode` decides only what the surrounding surface already provides: a modal brings its own card
 * and its own way out, and the customer's history is a sibling button in the list rather than a
 * modal opened on top of a modal.
 */
const ConversationDetail = props => {
  // The route param when routed; an explicit prop when embedded, where there is no route to read.
  const id = props.id || props.match?.params?.id
  const asModal = props.mode === 'modal'
  const [loading, setLoading] = useState(false)
  const [detail, setDetail] = useState(null)

  const [editingCar, setEditingCar] = useState(null)
  const [finalizingId, setFinalizingId] = useState(null)
  const [triggerSteps, setTriggerSteps] = useState([])
  // The other services' steps act on the conversation rather than on a car, and the list depends
  // on which service this is — so it loads once the detail says.
  const [serviceSteps, setServiceSteps] = useState([])
  const [serviceStep, setServiceStep] = useState(undefined)
  const [sendingStep, setSendingStep] = useState(false)
  const [triggeringId, setTriggeringId] = useState(null)
  const [addingCar, setAddingCar] = useState(false)

  // The same body the customers page shows, so an agent can see this person's whole history
  // — past conversations, cars, payments — without leaving the chat they are reading.
  const [historyOpen, setHistoryOpen] = useState(false)

  const [viewerOpen, setViewerOpen] = useState(false)
  const [viewerUrl, setViewerUrl] = useState(null)

  const [txnStatusDraft, setTxnStatusDraft] = useState({})
  const [savingTxnId, setSavingTxnId] = useState(null)
  const [timelineTxn, setTimelineTxn] = useState(null)

  const [showOfflineModal, setShowOfflineModal] = useState(false)
  const [savingOffline, setSavingOffline] = useState(false)
  const blankOffline = {
    amount: null,
    currency: 'GHS',
    method: 'CASH',
    status: 'SUCCESSFUL',
    note: '',
    carRequest: undefined
  }
  const [offline, setOffline] = useState(blankOffline)

  const getDetail = async () => {
    const response = await getRequest(
      `${dutyConversationsUrl}/${id}/`,
      'conversation',
      setLoading
    )
    if (response.success) {
      setDetail(response.data)
    }
  }

  useEffect(() => {
    getDetail()
    // eslint-disable-next-line
  }, [id])

  // Load the manual-trigger step list once (slots + actions with friendly labels).
  useEffect(() => {
    const loadSteps = async () => {
      const res = await getRequest(
        dutyCarRequestTriggerStepsUrl,
        'trigger steps',
        () => {}
      )
      if (res.success) setTriggerSteps(res.data.steps || [])
    }
    loadSteps()
  }, [])

  // The backend stores the WhatsApp number with the country code but no leading '+'. For a
  // username-only user (spec: whatsapp-bsuid-support) waId holds a BSUID (contains letters), not a
  // phone — in that case there is no phone to call/copy, so we show the identity label instead.
  // A merchant payment sits INITIALIZED until someone confirms the money arrived; if one is
  // open, logging a fresh payment for this customer would double-count it.
  const pendingMerchantTxn = (detail?.transactions || []).find(
    t => t.paymentMode === 'merchant' && t.status !== 'SUCCESSFUL'
  )

  const isPhone = !!detail?.waId && !/[a-zA-Z]/.test(detail.waId)
  const rawNumber = isPhone ? detail.waId.replace(/\D/g, '') : ''
  const phonePlus = rawNumber ? `+${rawNumber}` : ''
  // An app customer gave us a phone number, not a WhatsApp thread. It is very often the same
  // number — but "they verified this number in the app" is not "they will see a WhatsApp message",
  // and the contact controls on this page cannot tell the difference on their own.
  const appChannel = isAppChannel(detail?.channel)
  // Duty's own controls (cars, steps, offline payments) act on duty cars; a history or tracking
  // conversation has none, so it shows its order or shipment instead.
  const isDuty = (detail?.service || 'duty') === 'duty'
  // Pulled out of the effect below rather than read off `detail` inside it: a dependency array
  // has to be something the linter can check, and `[detail && detail.service]` is not.
  const conversationService = (detail && detail.service) || ''

  // Add a new blank car section to this conversation (admin takeover), then open it for editing.
  const handleAddCar = async () => {
    setAddingCar(true)
    const res = await postRequest(
      `${dutyCarRequestsUrl}/`,
      { conversation: id },
      'car',
      () => {}
    )
    setAddingCar(false)
    if (res.success) {
      successNotification('New car section added — fill in the details and finalize.')
      await getDetail()
      setEditingCar(res.data)
    } else {
      errorNotification('Could not add a car section, retry.')
    }
  }

  const handleFinalize = async car => {
    setFinalizingId(car.id)
    const res = await postRequest(
      dutyCarRequestFinalizeUrl(car.id),
      {},
      'finalization',
      () => {}
    )
    setFinalizingId(null)
    if (res.success) {
      successNotification(
        'Finalization queued. Refresh in a moment to see the result.'
      )
      getDetail()
    } else {
      const data = res.error && res.error.response && res.error.response.data
      if (data && data.missing && data.missing.length) {
        errorNotification(`Not ready — still missing: ${data.missing.join(', ')}`)
      }
    }
  }

  // The steps for a vehicle-history or tracking conversation.
  useEffect(() => {
    if (!conversationService || conversationService === 'duty') {
      setServiceSteps([])
      return
    }
    const loadSteps = async () => {
      const res = await getRequest(
        dutyConversationTriggerStepsUrl(conversationService),
        'steps',
        () => {}
      )
      if (res.success) setServiceSteps(res.data.steps || [])
    }
    loadSteps()
  }, [conversationService])

  const handleServiceTrigger = async () => {
    if (!serviceStep) return
    setSendingStep(true)
    const res = await postRequest(
      dutyConversationTriggerUrl(id),
      { step: serviceStep },
      'step',
      () => {}
    )
    setSendingStep(false)
    if (res.success) {
      successNotification(res.data?.detail || 'Step sent to the customer')
      setServiceStep(undefined)
      getDetail()
    } else {
      const data = res.error && res.error.response && res.error.response.data
      errorNotification((data && data.detail) || 'Could not send that step')
    }
  }

  const handleTrigger = async (car, step) => {
    setTriggeringId(car.id)
    const res = await postRequest(
      dutyCarRequestTriggerUrl(car.id),
      { step },
      'step',
      () => {}
    )
    setTriggeringId(null)
    if (res.success) {
      successNotification(res.data?.detail || 'Step sent to the customer')
      getDetail()
    } else {
      const data = res.error && res.error.response && res.error.response.data
      errorNotification((data && data.detail) || 'Could not trigger that step')
    }
  }

  const handleTxnStatusSave = async txn => {
    const newStatus = txnStatusDraft[txn.id] || txn.status
    if (newStatus === txn.status) return
    setSavingTxnId(txn.id)
    const res = await patchRequest(
      `${dutyTransactionsUrl}/${txn.id}/`,
      { status: newStatus },
      'transaction',
      () => {}
    )
    setSavingTxnId(null)
    if (res.success) {
      successNotification('Transaction status updated')
      getDetail()
    }
  }

  const handleCreateOffline = async () => {
    if (!offline.amount) {
      errorNotification('Enter an amount')
      return
    }
    const res = await postRequest(
      `${dutyTransactionsUrl}/`,
      offline,
      'transaction',
      setSavingOffline
    )
    if (res.success) {
      successNotification('Offline payment logged')
      setShowOfflineModal(false)
      setOffline(blankOffline)
      getDetail()
    }
  }

  const carLabel = car => {
    const s = car.slots || {}
    const parts = ['car_year', 'make', 'model']
      .map(k => s[k] && s[k].value)
      .filter(Boolean)
    return `Car ${car.position}${parts.length ? ` — ${parts.join(' ')}` : ''}`
  }

  return (
    <PageLoader className={asModal ? '' : 'container'} loading={loading}>
      <div className={asModal ? '' : 'card mb-5rem'}>
        <div className={asModal ? '' : 'card-body'}>
          {/*
            Pinned to the top of whatever is scrolling. On a phone the controls that act on this
            conversation were otherwise a full screen of cars and messages away from the part being
            read — and Refresh is the one you want exactly when you have scrolled to something
            stale. No Back button: the page only ever opens in a new tab, so there is nothing
            behind it to go back to, and in a modal the way out is the modal's own.
          */}
          <Space
            wrap
            style={{
              marginBottom: 16,
              position: 'sticky',
              top: 0,
              zIndex: 5,
              background: 'inherit',
              paddingTop: 4,
              paddingBottom: 4
            }}
          >
            <Button icon={<ReloadOutlined />} loading={loading} onClick={getDetail}>
              Refresh
            </Button>
            {/* In a modal the list offers History beside the button that opened this, so a second
                copy here would stack a modal on a modal to show the same thing. */}
            {!asModal && detail && detail.customer ? (
              <Button
                icon={<HistoryOutlined />}
                onClick={() => setHistoryOpen(true)}
              >
                Customer history
              </Button>
            ) : null}
          </Space>

          {!detail ? (
            <Empty description="Conversation not found" />
          ) : (
            <>
              <Descriptions title="Conversation" column={2} bordered size="small">
                {/*
                  First, because it qualifies everything under it. An app conversation is the same
                  kind of row as a WhatsApp one — same table, same cars, same finalizer — but the
                  customer is not necessarily reachable in a WhatsApp thread, and every contact
                  control on this page assumes they are.
                */}
                <Descriptions.Item label="Channel">
                  {channelTag(detail.channel)}
                </Descriptions.Item>
                <Descriptions.Item label={appChannel ? 'Phone' : 'WhatsApp'}>
                  {rawNumber ? (
                    <Space wrap>
                      <Typography.Text copyable={{ text: phonePlus }}>
                        {phonePlus}
                      </Typography.Text>
                      <Button
                        size="small"
                        icon={<PhoneOutlined />}
                        href={`tel:${phonePlus}`}
                      >
                        Call
                      </Button>
                      <Button
                        size="small"
                        type="primary"
                        icon={<WhatsAppOutlined />}
                        href={`https://wa.me/${rawNumber}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          backgroundColor: '#25D366',
                          borderColor: '#25D366'
                        }}
                      >
                        WhatsApp
                      </Button>
                    </Space>
                  ) : (
                    <IdentityLabel value={detail} />
                  )}
                </Descriptions.Item>
                {/*
                  What this conversation is ABOUT (spec: assistant-front-door Req 2.2). Beside the
                  channel because the two together are what decides how to reply: the surface says
                  whether a WhatsApp message reaches them, the service says what to say.
                */}
                <Descriptions.Item label="Service">
                  <Space wrap>
                    {serviceTag(detail.service)}
                    {detail.switchedTo ? (
                      <ConversationLink
                        id={detail.switchedToConversation}
                        service={detail.switchedTo}
                        label={`Switched to ${serviceLabel(detail.switchedTo)}`}
                      />
                    ) : null}
                    {detail.startedFrom ? (
                      <ConversationLink
                        id={detail.startedFrom}
                        label="Started from the previous conversation"
                      />
                    ) : null}
                    {/* The car a decision or hand-off brought in (spec 0040 Req 3.3). */}
                    {detail.carriedCar ? (
                      <Tag>Car carried over: {detail.carriedCar}</Tag>
                    ) : null}
                    {/* Unfinished work left for another service (spec 0040 Req 7). */}
                    {detail.parked ? (
                      <Tag color={detail.parked.expired ? 'default' : 'gold'}>
                        {parkedLine(detail.parked)}
                      </Tag>
                    ) : null}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="State">
                  <Tag color={STATE_COLORS[detail.state] || 'default'}>
                    {detail.state}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Customer">
                  {detail.customerName || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Last activity">
                  {detail.lastInboundAt
                    ? new Date(detail.lastInboundAt).toLocaleString()
                    : '—'}
                </Descriptions.Item>
              </Descriptions>

              <Divider orientation="left">Message customer</Divider>
              <div style={{ marginBottom: 12 }}>
                <MessageComposer
                  conversationId={id}
                  appChannel={appChannel}
                  onSent={getDetail}
                />
              </div>

              {/* A history or tracking conversation: what it is selling and where it has got to,
                  with the order or shipment one tap away. It has no duty cars. */}
              {!isDuty ? (
                <>
                  <Divider orientation="left">
                    {detail.service === 'tracking' ? 'Shipment' : 'Report order'}
                  </Divider>
                  <ServiceRecordCard
                    service={detail.service}
                    record={detail.serviceRecord}
                    progress={detail.progress}
                  />
                </>
              ) : null}

              {isDuty || (detail.carRequests || []).length ? (
                <>
              <Divider orientation="left">Cars</Divider>
              <div style={{ marginBottom: 12 }}>
                <Button
                  type="dashed"
                  loading={addingCar}
                  onClick={handleAddCar}
                >
                  + Add car section
                </Button>
                <span style={{ color: '#888', fontSize: 12, marginLeft: 8 }}>
                  Add another car on the customer&apos;s behalf (e.g. for an offline
                  payment you&apos;ll finalize).
                </span>
              </div>
              {(detail.carRequests || []).length ? (
                detail.carRequests.map(car => (
                  <CarPanel
                    key={car.id}
                    car={car}
                    onEdit={setEditingCar}
                    onFinalize={handleFinalize}
                    finalizing={finalizingId === car.id}
                    triggerSteps={triggerSteps}
                    onTrigger={handleTrigger}
                    triggering={triggeringId === car.id}
                  />
                ))
              ) : (
                <Empty description="No cars" />
              )}
                </>
              ) : null}

              <Divider orientation="left">Transactions</Divider>
              {/* Logging an offline payment attaches it to a duty car, so it is duty's only. */}
              <div style={{ marginBottom: 12, display: isDuty ? undefined : 'none' }}>
                <Button onClick={() => setShowOfflineModal(true)}>
                  + Log Offline Payment
                </Button>
              </div>
              {(detail.transactions || []).length ? (
                detail.transactions.map(t => (
                  <div
                    key={t.id || t.reference}
                    style={{
                      marginBottom: 8,
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: 8
                    }}
                  >
                    <Tag color={txnColor(t.status)}>{t.status}</Tag>
                    <span>
                      {t.currency} {t.amount}
                    </span>
                    {t.paymentMode === 'merchant' && claimedName(t.note) ? (
                      <span style={{ color: '#888', fontSize: 12 }}>
                        claimed by {claimedName(t.note)}
                      </span>
                    ) : null}
                    {t.method ? <Tag>{t.method}</Tag> : null}
                    {t.paymentMode ? (
                      <Tag color={PAYMENT_MODE_COLORS[t.paymentMode] || 'default'}>
                        {PAYMENT_MODE_LABELS[t.paymentMode] || t.paymentMode}
                      </Tag>
                    ) : null}
                    <code>{t.reference}</code>
                    {t.id ? (
                      <Space>
                        <Select
                          size="small"
                          style={{ width: 150 }}
                          value={txnStatusDraft[t.id] || t.status}
                          onChange={v =>
                            setTxnStatusDraft(d => ({ ...d, [t.id]: v }))
                          }
                        >
                          {TXN_STATUS_OPTIONS.map(s => (
                            <Option key={s} value={s}>
                              {s}
                            </Option>
                          ))}
                        </Select>
                        <Button
                          size="small"
                          type="primary"
                          loading={savingTxnId === t.id}
                          disabled={
                            (txnStatusDraft[t.id] || t.status) === t.status
                          }
                          onClick={() => handleTxnStatusSave(t)}
                        >
                          Save
                        </Button>
                        {t.status !== 'SUCCESSFUL' ? (
                          <Button size="small" onClick={() => setTimelineTxn(t)}>
                            Timeline
                          </Button>
                        ) : null}
                      </Space>
                    ) : null}
                  </div>
                ))
              ) : (
                <span style={{ color: '#888' }}>No transactions</span>
              )}

              <Divider orientation="left">Messages</Divider>
              <DecisionBanner decision={detail.openDecision} />
              <DecisionLog entries={detail.decisions} />
              {(detail.messages || []).length ? (
                <Timeline>
                  {detail.messages.map((m, i) => (
                    <Timeline.Item
                      key={i}
                      color={m.direction === 'in' ? 'blue' : 'green'}
                    >
                      <b>{m.direction === 'in' ? 'Customer' : 'Assistant'}</b>{' '}
                      <Tag>{m.type}</Tag>
                      {m.status ? <Tag>{m.status}</Tag> : null}
                      {m.type === 'image' ? (
                        m.imageUrl ? (
                          <div>
                            <img
                              src={m.imageUrl}
                              alt="customer upload"
                              style={{
                                maxWidth: 200,
                                maxHeight: 200,
                                cursor: 'pointer',
                                borderRadius: 6,
                                display: 'block'
                              }}
                              onClick={() => {
                                setViewerUrl(m.imageUrl)
                                setViewerOpen(true)
                              }}
                            />
                          </div>
                        ) : (
                          <div>
                            <i style={{ color: '#aaa' }}>
                              (image no longer available)
                            </i>
                          </div>
                        )
                      ) : (
                        /* `pre-wrap` because the text now arrives WHOLE, and a WhatsApp
                           message is written with line breaks in it — a duty breakdown is a
                           list, and a plain div collapses it into one paragraph. `break-word`
                           for the other direction: a long link with no spaces in it would
                           otherwise push the timeline wider than the page. */
                        <div
                          style={{
                            whiteSpace: 'pre-wrap',
                            overflowWrap: 'anywhere'
                          }}
                        >
                          {m.textPreview || <i>(no text)</i>}
                        </div>
                      )}
                      <small style={{ color: '#888' }}>
                        {m.createdAt
                          ? new Date(m.createdAt).toLocaleString()
                          : ''}
                      </small>
                    </Timeline.Item>
                  ))}
                </Timeline>
              ) : (
                <Empty description="No messages" />
              )}

              {/* The end of the transcript is where an agent decides what to do next, and the
                  furthest point from the controls at the top — so reply, nudge and reload are all
                  here too (owner, 2026-10-29), in one panel rather than a scroll back up. */}
              <Card
                size="small"
                style={{ marginTop: 16 }}
                title="Reply"
                extra={
                  <Button
                    size="small"
                    icon={<ReloadOutlined />}
                    loading={loading}
                    onClick={getDetail}
                  >
                    Refresh
                  </Button>
                }
              >
                <MessageComposer
                  compact
                  conversationId={id}
                  appChannel={appChannel}
                  onSent={getDetail}
                />
                {isDuty && (detail.carRequests || []).length ? (
                  <>
                    <Divider dashed style={{ margin: '12px 0' }} />
                    <div style={{ color: '#888', fontSize: 12, marginBottom: 6 }}>
                      Or send the customer a step — re-ask a question, resend the Pay Now
                      button, or finalize.
                    </div>
                    <StepTrigger
                      cars={detail.carRequests}
                      triggerSteps={triggerSteps}
                      onTrigger={handleTrigger}
                      triggeringId={triggeringId}
                      labelFor={carLabel}
                    />
                  </>
                ) : null}
                {/* The same help for a customer stuck in a history or tracking conversation
                    (owner, 2026-10-30): these steps act on the conversation, not on a car. */}
                {!isDuty && serviceSteps.length ? (
                  <>
                    <Divider dashed style={{ margin: '12px 0' }} />
                    <div style={{ color: '#888', fontSize: 12, marginBottom: 6 }}>
                      Or send the customer a step — ask the open question again, or move them
                      on.
                    </div>
                    <Space wrap>
                      <Select
                        showSearch
                        allowClear
                        optionFilterProp="label"
                        placeholder="Trigger a step"
                        style={{ minWidth: 260 }}
                        value={serviceStep}
                        onChange={setServiceStep}
                        options={serviceSteps.map(s => ({
                          value: s.value,
                          label: s.label
                        }))}
                      />
                      <Button
                        disabled={!serviceStep}
                        loading={sendingStep}
                        onClick={handleServiceTrigger}
                      >
                        Send step
                      </Button>
                    </Space>
                  </>
                ) : null}
              </Card>
            </>
          )}
        </div>
      </div>

      {/* Full-size viewer for a customer image (click a transcript thumbnail). */}
      {viewerUrl && (
        <LightBox
          images={[viewerUrl]}
          openSlideShow={viewerOpen}
          setOpenSlideShow={open => {
            if (!open) {
              setViewerOpen(false)
              setViewerUrl(null)
            }
          }}
        />
      )}

      {/* Edit car details */}
      <Modal
        title={editingCar ? carLabel(editingCar) : 'Edit Car'}
        open={!!editingCar}
        onCancel={() => setEditingCar(null)}
        footer={null}
        width={760}
        destroyOnClose
      >
        {editingCar && (
          <CarEditForm
            car={editingCar}
            onDone={() => {
              setEditingCar(null)
              getDetail()
            }}
          />
        )}
      </Modal>

      {/* Log an offline / manual payment */}
      <Modal
        title="Log Offline Payment"
        open={showOfflineModal}
        onOk={handleCreateOffline}
        onCancel={() => setShowOfflineModal(false)}
        okText="Log Payment"
        confirmLoading={savingOffline}
        destroyOnClose
      >
        <p style={{ color: '#888' }}>
          Records a payment taken outside the online flow. This does not message the
          customer or generate the estimate — use a car&apos;s “Process / Finalize”
          button for that.
        </p>
        {/* A merchant payment already has a transaction waiting to be marked successful —
            logging a second one here would double-count the same money. */}
        {pendingMerchantTxn ? (
          <p
            style={{
              background: 'rgba(250, 173, 20, 0.12)',
              border: '1px solid #faad14',
              borderRadius: 4,
              padding: '8px 12px',
              marginBottom: 12
            }}
          >
            This customer already has a <b>merchant payment awaiting verification</b> (
            {pendingMerchantTxn.currency} {pendingMerchantTxn.amount}
            {claimedName(pendingMerchantTxn.note)
              ? `, claimed by ${claimedName(pendingMerchantTxn.note)}`
              : ''}
            ). Mark that one <b>SUCCESSFUL</b> in the Transactions section above instead of
            logging a new payment — logging one here would record the money twice.
          </p>
        ) : null}
        <label style={{ display: 'block', marginBottom: 4 }}>Car</label>
        <Select
          allowClear
          placeholder="Attach to a car (optional)"
          style={{ width: '100%', marginBottom: 12 }}
          value={offline.carRequest}
          onChange={v => setOffline(o => ({ ...o, carRequest: v }))}
        >
          {(detail && detail.carRequests ? detail.carRequests : []).map(car => (
            <Option key={car.id} value={car.id}>
              {carLabel(car)}
            </Option>
          ))}
        </Select>

        <label style={{ display: 'block', marginBottom: 4 }}>Amount</label>
        <InputNumber
          min={0}
          precision={2}
          style={{ width: '100%', marginBottom: 12 }}
          value={offline.amount}
          onChange={v => setOffline(o => ({ ...o, amount: v }))}
        />

        <label style={{ display: 'block', marginBottom: 4 }}>Currency</label>
        <Select
          style={{ width: '100%', marginBottom: 12 }}
          value={offline.currency}
          onChange={v => setOffline(o => ({ ...o, currency: v }))}
        >
          {CURRENCY_OPTIONS.map(c => (
            <Option key={c} value={c}>
              {c}
            </Option>
          ))}
        </Select>

        <label style={{ display: 'block', marginBottom: 4 }}>Method</label>
        <Select
          style={{ width: '100%', marginBottom: 12 }}
          value={offline.method}
          onChange={v => setOffline(o => ({ ...o, method: v }))}
        >
          {TXN_METHOD_OPTIONS.map(m => (
            <Option key={m.value} value={m.value}>
              {m.label}
            </Option>
          ))}
        </Select>

        <label style={{ display: 'block', marginBottom: 4 }}>Status</label>
        <Select
          style={{ width: '100%', marginBottom: 12 }}
          value={offline.status}
          onChange={v => setOffline(o => ({ ...o, status: v }))}
        >
          {TXN_STATUS_OPTIONS.map(s => (
            <Option key={s} value={s}>
              {s}
            </Option>
          ))}
        </Select>

        <label style={{ display: 'block', marginBottom: 4 }}>Note</label>
        <Input.TextArea
          rows={2}
          placeholder="e.g. Paid cash at the office on 29 Jun"
          value={offline.note}
          onChange={e => setOffline(o => ({ ...o, note: e.target.value }))}
        />
      </Modal>

      {/* Customer history — the same body as the customers page quick view. Page mode only; see
          the button above. */}
      <Modal
        open={historyOpen}
        onCancel={() => setHistoryOpen(false)}
        footer={null}
        width={880}
        title="Customer history"
        destroyOnClose
      >
        {historyOpen && detail && detail.customer ? (
          <CustomerDetailBody id={detail.customer} />
        ) : null}
      </Modal>

      <PaymentTimelineModal
        transaction={timelineTxn}
        open={!!timelineTxn}
        onClose={() => setTimelineTxn(null)}
      />
    </PageLoader>
  )
}

ConversationDetail.propTypes = {
  /** Which conversation, when embedded. Routed use reads it from `match` instead. */
  id: PropTypes.string,
  /** 'page' (default, routed) or 'modal' (embedded in the list's quick view). */
  mode: PropTypes.oneOf(['page', 'modal']),
  match: PropTypes.object,
  history: PropTypes.object
}

ConversationDetail.defaultProps = {
  mode: 'page'
}

export default withRouter(ConversationDetail)
